import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';

const PatientFlowContext = createContext();

export const usePatientFlow = () => {
  const context = useContext(PatientFlowContext);
  if (!context) {
    throw new Error('usePatientFlow must be used within PatientFlowProvider');
  }
  return context;
};

// Initialize state from localStorage (persists across refreshes & tabs)
const FLOW_STORAGE_KEY = 'treatx_patient_flow';

const getInitialFlowState = () => {
  if (typeof window !== 'undefined') {
    // Try localStorage first (survives refresh), fallback to sessionStorage
    const saved = localStorage.getItem(FLOW_STORAGE_KEY) || sessionStorage.getItem('patientFlowState');
    if (saved) {
      try {
        return JSON.parse(saved);
      } catch (error) {
        console.error('Error loading flow state:', error);
      }
    }
  }
  return {
    currentStep: null,
    selectedTreatment: null,
    selectedTreatmentId: null,
    intakeCompleted: false,
    clinicsSelected: [],
    selectedClinicIds: [],
    selectedOffer: null,
    paymentCompleted: false,
    journeyStartedAt: null
  };
};

export const PatientFlowProvider = ({ children }) => {
  const [flowState, setFlowState] = useState(getInitialFlowState);

  // Save flow state to localStorage (survives refresh) + sessionStorage (legacy compat)
  useEffect(() => {
    const json = JSON.stringify(flowState);
    localStorage.setItem(FLOW_STORAGE_KEY, json);
    sessionStorage.setItem('patientFlowState', json);
  }, [flowState]);

  const updateFlowState = useCallback((updates) => {
    setFlowState(prev => ({ ...prev, ...updates }));
  }, []);

  // Start a completely new journey - clears all previous state
  const startNewJourney = useCallback((treatmentId = null, treatmentName = null) => {
    localStorage.removeItem(FLOW_STORAGE_KEY);
    sessionStorage.removeItem('preselectedTreatment');
    sessionStorage.removeItem('preselectedTreatmentId');
    sessionStorage.removeItem('redirectAfterLogin');
    sessionStorage.removeItem('patientFlowState');
    
    const newState = {
      currentStep: 'treatment',
      selectedTreatment: treatmentName,
      selectedTreatmentId: treatmentId,
      intakeCompleted: false,
      clinicsSelected: [],
      selectedClinicIds: [],
      selectedOffer: null,
      paymentCompleted: false,
      journeyStartedAt: new Date().toISOString()
    };
    setFlowState(newState);
    return newState;
  }, []);

  // Continue existing journey (if valid)
  const continueJourney = useCallback(() => {
    // Check if there's a valid journey to continue
    if (flowState.journeyStartedAt && flowState.selectedTreatment) {
      return true;
    }
    return false;
  }, [flowState]);

  // Select a treatment (explicit action)
  const selectTreatment = useCallback((treatmentId, treatmentName) => {
    // If different treatment selected, reset the journey from this point
    if (flowState.selectedTreatmentId !== treatmentId) {
      setFlowState(prev => ({
        ...prev,
        selectedTreatment: treatmentName,
        selectedTreatmentId: treatmentId,
        // Reset downstream selections when treatment changes
        clinicsSelected: [],
        selectedClinicIds: [],
        selectedOffer: null,
        paymentCompleted: false,
        journeyStartedAt: prev.journeyStartedAt || new Date().toISOString()
      }));
    }
  }, [flowState.selectedTreatmentId]);

  // Select clinics (explicit action)
  const selectClinics = useCallback((clinicIds) => {
    setFlowState(prev => ({
      ...prev,
      clinicsSelected: clinicIds,
      selectedClinicIds: clinicIds, // Store for offers filtering
      // Reset downstream selections when clinics change
      selectedOffer: null,
      paymentCompleted: false
    }));
  }, []);

  // Get selected clinic IDs for offers filtering
  const getSelectedClinicIds = useCallback(() => {
    return flowState.selectedClinicIds || flowState.clinicsSelected || [];
  }, [flowState.selectedClinicIds, flowState.clinicsSelected]);

  // Full reset
  const resetFlow = useCallback(() => {
    localStorage.removeItem(FLOW_STORAGE_KEY);
    sessionStorage.removeItem('preselectedTreatment');
    sessionStorage.removeItem('preselectedTreatmentId');
    sessionStorage.removeItem('redirectAfterLogin');
    sessionStorage.removeItem('patientFlowState');
    
    setFlowState({
      currentStep: null,
      selectedTreatment: null,
      selectedTreatmentId: null,
      intakeCompleted: false,
      clinicsSelected: [],
      selectedClinicIds: [],
      selectedOffer: null,
      paymentCompleted: false,
      journeyStartedAt: null
    });
  }, []);

  const getNextStep = useCallback(() => {
    if (!flowState.selectedTreatment) return '/treatments';
    if (!flowState.intakeCompleted) return '/patient/intake-enhanced';
    if (flowState.clinicsSelected.length === 0) return '/patient/clinic-search';
    if (!flowState.selectedOffer) return '/patient/offers';
    if (!flowState.paymentCompleted) return '/patient/payment';
    return '/patient/dashboard';
  }, [flowState]);

  const value = {
    flowState,
    updateFlowState,
    resetFlow,
    getNextStep,
    // New methods for proper flow control
    startNewJourney,
    continueJourney,
    selectTreatment,
    selectClinics,
    getSelectedClinicIds
  };

  return (
    <PatientFlowContext.Provider value={value}>
      {children}
    </PatientFlowContext.Provider>
  );
};
